/**
 * Transactions Index Page
 *
 * Main page for viewing and managing accounting transactions.
 * Integrates FilterBar, TransactionTable, BulkActionsBar, and Pagination.
 *
 * Features:
 * - Transaction listing with pagination
 * - Filtering by date, status, owner, account, and search
 * - Row selection for bulk operations
 * - Inline classification with toast notifications
 * - Bulk classification
 * - Error boundaries for graceful failure handling
 *
 * @see types/accounting.ts for TransactionsPageProps
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Head, usePage, router, Link } from '@inertiajs/react';
import { Toaster, toast } from 'sonner';
import { Navbar } from '@/components';

import type {
  TransactionsPageProps,
  TransactionFilters,
  Owner,
} from '@/types/accounting';

import { TransactionTable } from './components/TransactionTable';
import { FilterBar } from './components/FilterBar';
import { BulkActionsBar } from './components/BulkActionsBar';
import { ReviewPanel } from './components/ReviewPanel';
import {
  ErrorBoundary,
  FilterErrorFallback,
  TableErrorFallback,
  Pagination,
} from '../shared';

import { AccountTreeSelect } from '../shared/AccountTreeSelect';
import type { Transaction } from '@/types/accounting';
import { useAccountingTagsStore } from '@/stores/accountingTagsStore';
import { addTagToTransaction, removeTagFromTransaction } from '@/services/accountingTagsApi';
import type { Tag } from '@/types/accountingTags';

/**
 * Props for the classification modal
 */
interface ClassifyModalState {
  isOpen: boolean;
  selectedAccountId: number | null;
  owner: Owner;
}

/**
 * TransactionsIndex is the main page component for transaction management.
 *
 * Data Flow:
 * - Receives data from Inertia.js page props (server-side rendered)
 * - Local state for UI interactions (selection, modals)
 * - URL state for filters (via Inertia router)
 * - Optimistic updates for classification actions
 */
export default function TransactionsIndex() {
  // Get page props from Inertia
  const { transactions, accounts, accountTree, filters, pagination, stats } =
    usePage<TransactionsPageProps>().props;

  // Selection state (local)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Bulk classification modal state
  const [classifyModal, setClassifyModal] = useState<ClassifyModalState>({
    isOpen: false,
    selectedAccountId: null,
    owner: 'family',
  });

  // Loading states
  const [isBulkClassifying, setIsBulkClassifying] = useState(false);

  // Review panel state
  const [reviewingTransaction, setReviewingTransaction] = useState<Transaction | null>(null);

  // Sorting state (local, could be moved to URL)
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Tags state (Zustand store)
  const { tags: availableTags, fetchTags } = useAccountingTagsStore();

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Selected transactions for display
  const selectedTransactions = useMemo(
    () => transactions.filter((t) => selectedIds.has(t.id)),
    [transactions, selectedIds]
  );

  // ==========================================================================
  // Filter Handlers (URL state via Inertia)
  // ==========================================================================

  const updateFilters = useCallback(
    (newFilters: Partial<TransactionFilters>) => {
      // Build query params from current filters and updates
      const params: Record<string, unknown> = {
        ...filters,
        ...newFilters,
        page: 1, // Reset to first page on filter change
      };

      // Convert to URL-friendly format
      const query: Record<string, string | string[]> = {};
      if (params.dateFrom) query.date_from = params.dateFrom as string;
      if (params.dateTo) query.date_to = params.dateTo as string;
      if (params.accountId) query.account_id = String(params.accountId);
      if (params.owner) query.owner = params.owner as string;
      if (params.search) query.search = params.search as string;
      if (params.classificationStatus && (params.classificationStatus as string[]).length > 0) {
        query.status = params.classificationStatus as string[];
      }
      query.page = String(params.page || 1);

      router.get('/accounting/transactions', query, {
        preserveState: true,
        preserveScroll: false,
        only: ['transactions', 'pagination', 'stats'],
      });
    },
    [filters]
  );

  const clearFilters = useCallback(() => {
    router.get('/accounting/transactions', { page: 1 }, {
      preserveState: true,
      preserveScroll: false,
    });
  }, []);

  // ==========================================================================
  // Classification Handlers
  // ==========================================================================

  const handleClassify = useCallback(
    async (transactionId: number, accountId: number, owner: string) => {
      // Use fetch for API call (Inertia.js can also work but fetch is cleaner for APIs)
      try {
        const response = await fetch(
          `/api/accounting/transactions/${transactionId}/classify`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token':
                document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
            },
            body: JSON.stringify({
              accountId,
              owner,
              createRule: false,
            }),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to classify transaction');
        }

        toast.success('Transaction classified');

        // Reload transactions to get updated data
        router.reload({ only: ['transactions', 'stats'] });
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to classify transaction'
        );
        throw error;
      }
    },
    []
  );

  const handleBulkClassify = useCallback(async () => {
    if (selectedIds.size === 0 || !classifyModal.selectedAccountId) {
      return;
    }

    setIsBulkClassifying(true);

    try {
      const response = await fetch('/api/accounting/transactions/bulk_classify', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          transactionIds: Array.from(selectedIds),
          accountId: classifyModal.selectedAccountId,
          owner: classifyModal.owner,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to classify transactions');
      }

      const result = await response.json();
      toast.success(`${result.count} transactions classified`);

      // Clear selection and close modal
      setSelectedIds(new Set());
      setClassifyModal({ isOpen: false, selectedAccountId: null, owner: 'family' });

      // Reload transactions
      router.reload({ only: ['transactions', 'stats'] });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to classify transactions'
      );
    } finally {
      setIsBulkClassifying(false);
    }
  }, [selectedIds, classifyModal]);

  // ==========================================================================
  // Review Panel Handler
  // ==========================================================================

  const handleReviewTransaction = useCallback((transaction: Transaction) => {
    setReviewingTransaction(transaction);
  }, []);

  const handleReviewAccept = useCallback(() => {
    // Reload transactions after classification
    router.reload({ only: ['transactions', 'stats'] });
  }, []);

  // ==========================================================================
  // Selection Handlers
  // ==========================================================================

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(transactions.map((t) => t.id)));
  }, [transactions]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // ==========================================================================
  // Tag Handlers
  // ==========================================================================

  const handleAddTag = async (transactionId: number, tag: Tag) => {
    await addTagToTransaction(transactionId, `${tag.tagType}:${tag.value}`);
    toast.success('Tag added');
    router.reload({ only: ['transactions'] });
  };

  const handleRemoveTag = async (transactionId: number, tagging: { tag: { tagType: string, value: string } }) => {
    await removeTagFromTransaction(transactionId, `${tagging.tag.tagType}:${tagging.tag.value}`);
    toast.success('Tag removed');
    router.reload({ only: ['transactions'] });
  };

  // ==========================================================================
  // Sorting Handler
  // ==========================================================================

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        // Toggle direction
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortDirection('desc');
      }
      // Note: In a full implementation, sorting would be handled server-side
      // and we'd use updateFilters to pass sort params
    },
    [sortColumn]
  );

  // ==========================================================================
  // Pagination Handler
  // ==========================================================================

  const handlePageChange = useCallback(
    (page: number) => {
      const query: Record<string, string | string[]> = { page: String(page) };

      // Preserve current filters
      if (filters.dateFrom) query.date_from = filters.dateFrom;
      if (filters.dateTo) query.date_to = filters.dateTo;
      if (filters.accountId) query.account_id = String(filters.accountId);
      if (filters.owner) query.owner = filters.owner;
      if (filters.search) query.search = filters.search;
      if (filters.classificationStatus && filters.classificationStatus.length > 0) {
        query.status = filters.classificationStatus;
      }

      router.get('/accounting/transactions', query, {
        preserveState: true,
        preserveScroll: true,
        only: ['transactions', 'pagination'],
      });
    },
    [filters]
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <>
      <Head title="Transactions" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="transactions-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Transactions</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage and classify your financial transactions
              </p>
            </div>
            <nav className="flex items-center gap-2">
              <Link
                href="/accounting/suggested_rules"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Review Rules
              </Link>
              <Link
                href="/accounting/classification_rules"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Rules
              </Link>
              <Link
                href="/accounting/classification"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Metrics
              </Link>
              <Link
                href="/accounting/import"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Import
              </Link>
            </nav>
          </div>
        </header>

        {/* Filter Bar */}
        <ErrorBoundary fallback={<FilterErrorFallback />}>
          <FilterBar
            filters={filters}
            stats={stats}
            accounts={accounts}
            onFilterChange={updateFilters}
            onClear={clearFilters}
          />
        </ErrorBoundary>

        {/* Main Content */}
        <main className="px-6 py-4">
          <ErrorBoundary fallback={<TableErrorFallback />}>
            <TransactionTable
              transactions={transactions}
              accounts={accounts}
              accountTree={accountTree}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onClassify={handleClassify}
              onReview={handleReviewTransaction}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              availableTags={availableTags}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
            />
          </ErrorBoundary>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Showing {transactions.length} of {pagination.totalCount} transactions
              </p>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                siblingCount={1}
                showFirstLast
              />
            </div>
          )}

          {/* Single page info */}
          {pagination.totalPages <= 1 && transactions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </main>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <BulkActionsBar
            selectedCount={selectedIds.size}
            totalCount={transactions.length}
            onClassify={() =>
              setClassifyModal((prev) => ({ ...prev, isOpen: true }))
            }
            onClearSelection={handleClearSelection}
            onSelectAll={handleSelectAll}
            isLoading={isBulkClassifying}
          />
        )}

        {/* Bulk Classification Modal */}
        {classifyModal.isOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() =>
              setClassifyModal({ isOpen: false, selectedAccountId: null, owner: 'family' })
            }
            data-testid="bulk-classify-modal-backdrop"
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="bulk-classify-modal"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Classify {selectedIds.size} Transaction{selectedIds.size !== 1 ? 's' : ''}
              </h2>

              {/* Account Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Category
                </label>
                <AccountTreeSelect
                  accounts={accounts}
                  accountTree={accountTree}
                  value={classifyModal.selectedAccountId}
                  onChange={(id) =>
                    setClassifyModal((prev) => ({ ...prev, selectedAccountId: id }))
                  }
                  filterTypes={['expense', 'income']}
                  placeholder="Select a category..."
                  disabled={isBulkClassifying}
                />
              </div>

              {/* Owner Toggle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner
                </label>
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="bulk-owner"
                      checked={classifyModal.owner === 'family'}
                      onChange={() =>
                        setClassifyModal((prev) => ({ ...prev, owner: 'family' }))
                      }
                      disabled={isBulkClassifying}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Family</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="bulk-owner"
                      checked={classifyModal.owner === 'estate'}
                      onChange={() =>
                        setClassifyModal((prev) => ({ ...prev, owner: 'estate' }))
                      }
                      disabled={isBulkClassifying}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Estate</span>
                  </label>
                </div>
              </div>

              {/* Preview of selected transactions */}
              {selectedTransactions.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-gray-500 mb-2">Selected:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {selectedTransactions.slice(0, 5).map((t) => (
                      <li key={t.id} className="truncate">
                        {t.description} - {t.amountDisplay}
                      </li>
                    ))}
                    {selectedTransactions.length > 5 && (
                      <li className="text-gray-400 italic">
                        ...and {selectedTransactions.length - 5} more
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setClassifyModal({ isOpen: false, selectedAccountId: null, owner: 'family' })
                  }
                  disabled={isBulkClassifying}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="bulk-classify-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkClassify}
                  disabled={isBulkClassifying || !classifyModal.selectedAccountId}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed"
                  data-testid="bulk-classify-confirm"
                >
                  {isBulkClassifying ? 'Classifying...' : 'Classify All'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Panel */}
        {reviewingTransaction && (
          <ReviewPanel
            transaction={reviewingTransaction}
            accounts={accounts}
            accountTree={accountTree}
            onClose={() => setReviewingTransaction(null)}
            onAccept={handleReviewAccept}
          />
        )}
      </div>
    </>
  );
}
